import React from 'react';
import { Col, Row, Typography } from 'antd';
import { WrapperPopover } from './WrapperFactsPanelItem';
import { getUomTypesAbbreviations } from '../../../nutrition-label/DailyValueConstants';
import { useGetNutritionEnum } from '../../../nutrition-label/hook/useNutritionLabel';
import { MEASUREMENT_UNIT } from '../../../nutrition-label/constants';
import {
  getNameAndItalicizeNameSupplement,
  renderDescription,
  renderName,
} from '../../../supplement-label/utils';
import { parseNumber } from 'utils/parser';
import { InfoWithLabel } from 'common/components';

const { Text } = Typography;

export const WrapperFactsPanel = (props) => {
  const { label = [], currentValue, originValue, children } = props;

  if (!originValue) return <>{children}</>;

  const isHightLightText = label?.some(
    (item) => currentValue?.[item] !== originValue?.[item]
  );

  return (
    <div style={{ backgroundColor: isHightLightText ? '#e6f7ff' : '' }}>
      {children}
    </div>
  );
};

export const WrapperLinearLabel = (props) => {
  const { label = [], currentValue, originValue, children } = props;

  if (!originValue) return <>{children}</>;

  const isHightLightText = label?.every(
    (item) => currentValue?.[item] !== originValue?.[item]
  );

  return (
    <span style={{ backgroundColor: isHightLightText ? '#e6f7ff' : '' }}>
      {children}
    </span>
  );
};

export const WrapperNutritionDailyField = (props) => {
  const { originValue, children } = props;

  if (!originValue) return <>{children}</>;

  return (
    <WrapperPopover
      content={<RenderItemView property={originValue} />}
      title='Origin value'
      trigger='hover'
      placement='left'
    >
      <div
        style={{ outline: 'solid 3px rgba(0, 112, 204, 0.4)', borderRadius: 4 }}
      >
        {children}
      </div>
    </WrapperPopover>
  );
};

const RenderItemView = ({ property }) => {
  const { basicUomProperties } = useGetNutritionEnum();
  return (
    <>
      {!!property?.path ? (
        <Row>
          <Col style={{ flex: 1 }}>
            <Row
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                flex: 1,
                marginBottom: 7,
              }}
            >
              <Col xs={24}>
                <span
                  style={{ wordBreak: 'break-inherit', fontWeight: 'bold' }}
                >
                  {property.nutritionTypeCode}
                </span>
              </Col>
            </Row>
            <Row>
              <Col flex={18}>
                <span>{property.measurementPrecisionCode}</span>
              </Col>
              <Col flex={4}>
                {`${parseNumber(property.quantity)}${getUomTypesAbbreviations(
                  basicUomProperties,
                  property.quantityUom
                )}`}
              </Col>
              <Col flex={2} style={{ textAlign: 'right' }}>
                {parseNumber(property.dailyValueIntakePercent)}%
              </Col>
            </Row>
          </Col>
        </Row>
      ) : (
        <div>N/A</div>
      )}
    </>
  );
};

export const WrapperSupplementDailyField = (props) => {
  const { originValue, fieldName, children } = props;

  if (!originValue) return <>{children}</>;

  return (
    <WrapperPopover
      content={<RenderViewMode item={originValue} fieldName={fieldName} />}
      title='Origin value'
      trigger='hover'
      placement='left'
    >
      <div
        style={{ outline: 'solid 3px rgba(0, 112, 204, 0.4)', borderRadius: 4 }}
      >
        {children}
      </div>
    </WrapperPopover>
  );
};

const RenderViewMode = (props) => {
  const { item, fieldName } = props;

  const convertUom = (unit) => {
    return MEASUREMENT_UNIT[unit?.toLowerCase()] || unit;
  };

  const renderQuantity = () => {
    const convertedUom = item.quantityUom ? convertUom(item.quantityUom) : '';

    const quantity =
      item.quantity === null || item.quantity === undefined
        ? ''
        : `${Math.round(item.quantity * 100) / 100}${convertedUom}`;

    const description = renderDescription(item?.quantityDescription, false);

    return [quantity, description].filter(Boolean).join(' ');
  };

  const renderDailyValue = () => {
    return `${
      item.dailyValue !== undefined && item.dailyValue !== null
        ? item.dailyValue + '%'
        : ''
    }${item.dailySymbol ? item.dailySymbol : ''}`;
  };

  const { name, italicName } = getNameAndItalicizeNameSupplement(
    item,
    fieldName
  );

  return (
    <>
      {!!item?.path ? (
        <>
          <Row style={{ width: 400 }}>
            <Col span={16}>
              <Text
                style={{ fontWeight: 700 }}
                ellipsis={{
                  tooltip: `${renderName(name, false)} ${renderDescription(
                    item?.descriptor,
                    false
                  )}`,
                }}
              >
                {renderName(name, italicName)}
                {renderDescription(item?.descriptor, item?.italicDescriptor)}
              </Text>
            </Col>
            <Col span={4}>
              <Text>{renderQuantity()}</Text>
            </Col>
            <Col span={4} style={{ textAlign: 'right' }}>
              <Text>{renderDailyValue()}</Text>
            </Col>
          </Row>
          {item?.ingredientsPartOf ? (
            <InfoWithLabel
              showTooltip
              label='Part Of'
              info={item?.ingredientsPartOf}
              labelColProps={{ span: 4 }}
              infoColProps={{ span: 20 }}
            />
          ) : null}
        </>
      ) : (
        <div>N/A</div>
      )}
    </>
  );
};
